import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Group as GroupIcon
} from '@mui/icons-material';

function GlobalListColumn({
    listType, items, newItemUrl, onNewItemUrlChange, onAddItem, onDelete,
    loading, onOpenGroupDialog, loadingGroups
}) {
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const isWhitelist = listType === 'whitelist';
    const accentColor = isWhitelist ? '#2e7d32' : '#c62828';
    const headerBg = isWhitelist ? '#e8f5e9' : '#ffebee';
    const emptyMsg = isWhitelist
        ? 'Nessun elemento nella whitelist globale'
        : 'Nessun elemento nella blacklist globale';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Input aggiunta URL singolo */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                <TextField
                    label="URL (es. example.com)"
                    value={newItemUrl}
                    onChange={(e) => onNewItemUrlChange(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="example.com"
                    onKeyDown={(e) => { if (e.key === 'Enter') onAddItem(); }}
                    disabled={loading}
                />
                <Tooltip title="Aggiungi URL">
                    <span>
                        <IconButton
                            onClick={onAddItem}
                            disabled={!newItemUrl.trim() || loading}
                            size="small"
                            sx={{ border: 1, borderColor: accentColor, color: accentColor }}
                        >
                            <AddIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Pulsante selezione gruppo (spacchetta in URL singoli) */}
            <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<GroupIcon />}
                onClick={onOpenGroupDialog}
                disabled={loading || loadingGroups}
                sx={{
                    mb: 2, textTransform: 'none', fontWeight: 600,
                    color: accentColor,
                    borderColor: `${accentColor}50`,
                    '&:hover': { borderColor: accentColor, backgroundColor: `${accentColor}08` }
                }}
            >
                {loadingGroups ? 'Caricamento...' : 'Aggiungi da Gruppo'}
            </Button>

            {/* Tabella */}
            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2, textAlign: 'center' }}>
                    {emptyMsg}
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: headerBg, fontSize: '0.8rem' }}>URL</TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: headerBg, fontSize: '0.8rem', width: 50, textAlign: 'center' }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {item.url}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center', p: 0.5 }}>
                                        <Tooltip title="Elimina">
                                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(item)} disabled={loading}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog conferma eliminazione */}
            <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Conferma eliminazione</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Eliminare <strong><code>{deleteConfirm?.url}</code></strong> dalla {listType} globale?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Annulla</Button>
                    <Button color="error" variant="contained" onClick={() => {
                        onDelete(deleteConfirm.id, deleteConfirm.url);
                        setDeleteConfirm(null);
                    }}>
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function GlobalListsTable({
    whitelistItems = [],
    onDeleteWhitelist,
    newWhitelistUrl = '',
    onNewWhitelistUrlChange,
    onAddWhitelist,
    loading = false,
    onOpenGroupDialog,
    loadingGroups = false,
}) {
    return (
        <Box sx={{ maxWidth: 700 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                    Whitelist Globale
                </Typography>
                <Typography variant="caption" color="text.secondary">({whitelistItems.length})</Typography>
            </Box>
            <GlobalListColumn
                listType="whitelist"
                items={whitelistItems}
                newItemUrl={newWhitelistUrl}
                onNewItemUrlChange={onNewWhitelistUrlChange}
                onAddItem={onAddWhitelist}
                onDelete={onDeleteWhitelist}
                loading={loading}
                onOpenGroupDialog={onOpenGroupDialog}
                loadingGroups={loadingGroups}
            />
        </Box>
    );
}

GlobalListsTable.propTypes = {
    whitelistItems: PropTypes.array,
    onDeleteWhitelist: PropTypes.func.isRequired,
    newWhitelistUrl: PropTypes.string,
    onNewWhitelistUrlChange: PropTypes.func.isRequired,
    onAddWhitelist: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    onOpenGroupDialog: PropTypes.func,
    loadingGroups: PropTypes.bool,
};
